/**
 * <cos-leanpay> — native LeanPay financing widget (replaces the LeanPay app-block).
 *
 * Renders an inline teaser ("în rate de la X Lei/lună") under the buy button
 * plus a <dialog class="modal"> calculator: term pills, optional credit
 * insurance, fixed-rate + DAE breakdown, and a "Verifică limita" CTA that
 * deep-links to LeanPay prequalify WITH a redirect-back to this PDP.
 *
 * Installment math = standard annuity, validated EXACT against LeanPay's
 * regulatory representative example (5000 Lei / 60mo → 149.52 no-insurance /
 * 140.57 with-insurance). See memory project_leanpay_widget_redesign_2026_05_29.
 *
 * Data source is intentionally swappable: _calc() runs locally now (decision A,
 * zero backend). To move to live API (decision B), swap _plansFor() to fetch
 * POST /vendor/installment-plans via a Shopify App Proxy — the render layer
 * stays identical.
 *
 * Config arrives as JSON in data-settings (skill cos-theme/data-settings-pattern).
 * Modal open / close / scroll-lock / mobile bottom-sheet / focus-return are all
 * handled by modal-manager.js — this element only computes + paints values and
 * handles the pills + insurance toggle. State resets when the modal opens.
 */

import Delegate from 'ftdomdelegate';

class CosLeanpay extends HTMLElement {
  constructor() {
    super();
    this.cfg = null;
    this.defaultTerm = 12;
    this.state = { term: 12, ins: false };
    this.delegate = null;
    this.dialog = null;
    this.onOpen = null;
    this.refs = {};
  }

  connectedCallback() {
    try {
      this.cfg = JSON.parse(this.dataset.settings || '{}');
    } catch (err) {
      console.error('[cos-leanpay] invalid data-settings JSON', err);
      return;
    }
    if (!this.cfg.amount || !this.cfg.terms || !this.cfg.terms.length) return;

    // Default term: configured value if available, else 12, else longest.
    this.defaultTerm = this.cfg.terms.includes(this.cfg.defaultTerm)
      ? this.cfg.defaultTerm
      : this.cfg.terms.includes(12)
        ? 12
        : this.cfg.terms[this.cfg.terms.length - 1];
    this.state.term = this.defaultTerm;

    this.dialog = this.querySelector('dialog.modal');
    this._cacheRefs();
    this._paintTeaser();

    this.delegate = new Delegate(this);
    this.delegate.on('click', '[data-lp-term]', (e, target) => {
      this.state.term = parseInt(target.dataset.lpTerm, 10);
      this._paintModal();
    });
    this.delegate.on('click', '[data-lp-insurance]', () => this._toggleInsurance());
    this.delegate.on('keydown', '[data-lp-insurance]', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        this._toggleInsurance();
      }
    });

    // Reset to defaults each time the modal is opened (modal-manager fires this).
    if (this.dialog) {
      this.onOpen = () => {
        this.state = { term: this.defaultTerm, ins: false };
        this._paintModal();
      };
      this.dialog.addEventListener('modal:open', this.onOpen);
    }

    // Same-product variant price change. The theme's <variant-picker> fires a
    // bubbling `variant:change` on the product form (detail.variant.price is in
    // bani). The refurb catalog is split-variant (cross-product navigation), so
    // this is latent today, but a true multi-variant product would otherwise
    // leave the installment stale. One main product per PDP; product cards use a
    // different event (product:variant:changed), so we accept this globally.
    this._onVariantChange = (e) => {
      const v = e.detail && e.detail.variant;
      if (!v || typeof v.price !== 'number') return;
      this._setPrice(v.price);
    };
    document.addEventListener('variant:change', this._onVariantChange);

    // Addon-bundle total change (product-bundle-sync.js dispatches `bundle:change`
    // with detail.total = gross cart total in bani: base variant + every checked
    // addon). Financing follows the configured cart, so the calculator re-bases on
    // it. count 0 → total === base price, resetting LeanPay to the bare product.
    this._onBundleChange = (e) => {
      const total = e.detail && e.detail.total;
      if (typeof total !== 'number') return;
      this._setPrice(total);
    };
    document.addEventListener('bundle:change', this._onBundleChange);

    this._paintModal();
  }

  disconnectedCallback() {
    if (this.delegate) this.delegate.off();
    if (this.dialog && this.onOpen) this.dialog.removeEventListener('modal:open', this.onOpen);
    if (this._onVariantChange) document.removeEventListener('variant:change', this._onVariantChange);
    if (this._onBundleChange) document.removeEventListener('bundle:change', this._onBundleChange);
  }

  /**
   * Re-base the entire widget on a new GROSS price (bani) — shared by the
   * variant-change and addon-bundle paths. Recomputes the avans band + financed
   * principal, then repaints teaser, modal headline ("Pentru X Lei" + avans /
   * financed) and the breakdown. Centralising here fixed a latent bug: the
   * variant path used to leave the static headline lines stale.
   */
  _setPrice(priceBani) {
    if (typeof priceBani !== 'number' || priceBani <= 0) return;
    const price = Math.ceil(priceBani / 100);
    this.cfg.price = price;
    this.cfg.downpayment = this._downpaymentFor(price);
    this.cfg.amount = price - this.cfg.downpayment; // financed = price − avans
    this._paintTeaser();
    this._paintHeadline();
    this._paintModal();
  }

  _cacheRefs() {
    const q = (sel) => this.querySelector(sel);
    this.refs = {
      teaser: q('[data-lp-teaser]'),
      amountLine: q('[data-lp-amount-line]'),
      avansLine: q('[data-lp-avans-line]'),
      avans: q('[data-lp-avans]'),
      financed: q('[data-lp-financed]'),
      monthly: q('[data-lp-monthly]'),
      sub: q('[data-lp-sub]'),
      annual: q('[data-lp-annual]'),
      dae: q('[data-lp-dae]'),
      total: q('[data-lp-total]'),
      cta: q('[data-lp-cta]'),
      insurance: q('[data-lp-insurance]'),
      pills: Array.from(this.querySelectorAll('[data-lp-term]')),
    };
  }

  _toggleInsurance() {
    this.state.ins = !this.state.ins;
    this._paintModal();
  }

  /**
   * Downpayment (avans) for a rounded-up price, from the LeanPay band table in
   * cfg.bands ('threshold:avans,...', descending). Mirrors the Liquid loop in
   * leanpay-widget.liquid so a same-product variant price change stays correct
   * without a re-fetch — the bands string is the single source of both.
   */
  _downpaymentFor(price) {
    const bands = String(this.cfg.bands || '')
      .split(',')
      .map((b) => b.split(':').map(Number))
      .filter((p) => p.length === 2 && !Number.isNaN(p[0]));
    for (const [threshold, avans] of bands) {
      if (price >= threshold) return avans;
    }
    return 0;
  }

  /**
   * Annuity installment. P (financed principal) = amount + origination fee
   * [+ single insurance premium]. Reproduces the representative example to the bani.
   */
  _calc(amount, months, withInsurance) {
    const annual = withInsurance ? this.cfg.rateWithIns : this.cfg.rateNoIns;
    const insurance = withInsurance ? amount * this.cfg.insPct : 0;
    const principal = amount + this.cfg.fee + insurance;
    const r = annual / 12;
    const monthly = (principal * r) / (1 - Math.pow(1 + r, -months));
    return {
      monthly,
      total: monthly * months,
      dae: withInsurance ? this.cfg.daeWithIns : this.cfg.daeNoIns,
      annual,
    };
  }

  _fmt(value, decimals = 2) {
    return Number(value).toLocaleString(this.cfg.locale || 'ro-RO', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  // Whole-Lei money, e.g. "2.268 Lei" — matches the Liquid `money` headline.
  _money(lei) {
    const n = Math.round(Number(lei) || 0).toLocaleString(this.cfg.locale || 'ro-RO');
    return n + ' ' + (this.cfg.currency || 'Lei');
  }

  // Repaint the three server-rendered headline lines ("Pentru X Lei", avans,
  // financed) from the locale templates (data-settings, with an __AMT__ token).
  // Only called on a price change; the initial paint stays Liquid-rendered.
  _paintHeadline() {
    const r = this.refs;
    if (r.amountLine && this.cfg.forAmountTpl) {
      r.amountLine.textContent = this.cfg.forAmountTpl.replace('__AMT__', this._money(this.cfg.price));
    }
    if (r.avans && this.cfg.downpaymentTpl) {
      r.avans.textContent = this.cfg.downpaymentTpl.replace('__AMT__', this._money(this.cfg.downpayment));
    }
    if (r.financed && this.cfg.financedTpl) {
      r.financed.textContent = this.cfg.financedTpl.replace('__AMT__', this._money(this.cfg.amount));
    }
    if (r.avansLine) r.avansLine.hidden = !(this.cfg.downpayment > 0);
  }

  // Teaser = lowest advertisable monthly = longest term, cheaper of with/without
  // insurance ("de la X/lună"). With insurance the rate is lower (18.99 vs 24.99)
  // but adds a one-off premium; whichever nets lower is the honest minimum. Math.min
  // stays correct even if the merchant later edits the rates so the relationship flips.
  // Matches the /plata-in-rate page and LeanPay's own app ("de la 21,80% DAE").
  _paintTeaser() {
    if (!this.refs.teaser) return;
    const longest = Math.max(...this.cfg.terms);
    const monthly = Math.min(
      this._calc(this.cfg.amount, longest, false).monthly,
      this._calc(this.cfg.amount, longest, true).monthly
    );
    this.refs.teaser.textContent = Math.round(monthly).toLocaleString(this.cfg.locale || 'ro-RO');
  }

  _paintModal() {
    const c = this._calc(this.cfg.amount, this.state.term, this.state.ins);
    const currency = this.cfg.currency || 'Lei';

    if (this.refs.monthly) this.refs.monthly.textContent = this._fmt(c.monthly);
    if (this.refs.sub && this.refs.sub.dataset.lpSubTpl) {
      this.refs.sub.textContent = this.refs.sub.dataset.lpSubTpl
        .replace('{months}', String(this.state.term))
        .replace('{amount}', this._fmt(c.monthly));
    }
    if (this.refs.annual) this.refs.annual.textContent = this._fmt(c.annual * 100) + '%';
    if (this.refs.dae) this.refs.dae.textContent = this._fmt(c.dae) + '%';
    if (this.refs.total) this.refs.total.textContent = this._fmt(c.total) + ' ' + currency;
    if (this.refs.cta) this.refs.cta.href = this._prequalifyUrl();

    this.refs.pills.forEach((pill) =>
      pill.setAttribute('aria-pressed', String(parseInt(pill.dataset.lpTerm, 10) === this.state.term))
    );
    if (this.refs.insurance) this.refs.insurance.setAttribute('aria-checked', String(this.state.ins));
  }

  /**
   * LeanPay prequalify deep-link. The public prequalify URL is the per-vendor
   * link from the LeanPay panel (Company → Development → Pre-calificare URL),
   * already carrying ?vendor=<token>. It is a credit-limit check, not a product
   * checkout, so it takes no amount/returnUrl. Configured via
   * data-settings.prequalifyBase so the token can change without a code edit.
   */
  _prequalifyUrl() {
    return this.cfg.prequalifyBase || '#';
  }
}

if (!customElements.get('cos-leanpay')) {
  customElements.define('cos-leanpay', CosLeanpay);
}
